import { useCallback, useEffect, useState } from "react";
import { Loader2, Check, X, RefreshCw, Download } from "lucide-react";
import { useAdminAuth } from "../../../context/AdminAuthContext";
import { toast } from "../../../components/ui/sonner";

const STATUSES = ["PENDING", "APPROVED", "REJECTED", ""];

function formatINR(n) {
  return "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

export default function WithdrawalsTab() {
  const { authFetch, downloadCsv } = useAdminAuth();
  const [status, setStatus] = useState("PENDING");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [exporting, setExporting] = useState(false);

  const doExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      const path = `/api/admin/export/withdrawals.csv${params.toString() ? `?${params}` : ""}`;
      const name = await downloadCsv(path, "geonetix-withdrawals.csv");
      toast("CSV downloaded", { description: name });
    } catch (e) {
      toast("Export failed", { description: e.message });
    } finally {
      setExporting(false);
    }
  };

  const load = useCallback(
    async (st = status) => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (st) params.set("status", st);
        params.set("limit", "200");
        const data = await authFetch(`/api/admin/withdrawals?${params.toString()}`);
        setItems(data.items || []);
      } catch {
        /* handled */
      } finally {
        setLoading(false);
      }
    },
    [authFetch, status]
  );

  useEffect(() => {
    load(status);
  }, [status, load]);

  const act = async (id, action) => {
    setActing(id + action);
    try {
      const res = await authFetch(`/api/admin/withdrawals/${action}`, {
        method: "POST",
        body: JSON.stringify({ withdrawal_id: id }),
      });
      toast(`Withdrawal ${res.status}${res.email_sent ? " · email sent" : ""}`);
      load(status);
    } catch (e) {
      toast("Action failed", { description: e.message });
    } finally {
      setActing(null);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="inline-flex rounded-full border border-white/10 bg-black/20 p-1">
          {STATUSES.map((s) => (
            <button
              key={s || "all"}
              data-testid={`wd-filter-${(s || "all").toLowerCase()}`}
              onClick={() => setStatus(s)}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-widest transition-colors ${
                status === s ? "bg-[#00B4D8] text-[#04140a]" : "text-slate-300"
              }`}
            >
              {s || "all"}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-4">
          <button
            data-testid="wd-export-csv"
            onClick={doExport}
            disabled={exporting}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-semibold text-slate-200 hover:brightness-110 disabled:opacity-60"
          >
            {exporting ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} CSV
          </button>
          <button onClick={() => load(status)} className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-[#00B4D8]">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="animate-spin text-slate-400" />
        </div>
      ) : items.length === 0 ? (
        <p data-testid="wd-empty" className="text-slate-500 text-center py-16">
          No {status ? status.toLowerCase() : ""} withdrawals.
        </p>
      ) : (
        <div className="space-y-3" data-testid="wd-list">
          {items.map((w) => {
            const st = String(w.status || "").toLowerCase();
            return (
              <div key={w.id} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <span className="font-mono-gx font-bold text-lg">{formatINR(w.amount)}</span>
                    <span className="text-xs uppercase text-slate-500">{w.method || (w.upi_id ? "upi" : "bank")}</span>
                    <span
                      className={`text-xs font-semibold ${
                        st === "pending" ? "text-amber-300" : st === "approved" ? "text-[#4CAF50]" : "text-red-400"
                      }`}
                    >
                      {st}
                    </span>
                  </div>
                  <div className="text-sm text-slate-400 mt-1">
                    {w.user_email}
                    {w.user_full_name ? ` · ${w.user_full_name}` : ""}
                  </div>
                  <div className="text-xs text-slate-500 mt-1 break-all">
                    {w.upi_id
                      ? `UPI: ${w.upi_id}`
                      : `${w.account_name || ""} · A/C ${w.account_number || ""} · ${w.ifsc || ""}`}
                  </div>
                </div>
                {st === "pending" && (
                  <div className="flex gap-2">
                    <button
                      data-testid={`wd-approve-${w.id}`}
                      onClick={() => act(w.id, "approve")}
                      disabled={acting === w.id + "approve"}
                      className="inline-flex items-center gap-1.5 rounded-full bg-[#4CAF50] px-4 py-2 text-sm font-semibold text-[#04140a] hover:brightness-110 disabled:opacity-60"
                    >
                      <Check size={15} /> Approve
                    </button>
                    <button
                      data-testid={`wd-reject-${w.id}`}
                      onClick={() => act(w.id, "reject")}
                      disabled={acting === w.id + "reject"}
                      className="inline-flex items-center gap-1.5 rounded-full border border-red-400/50 text-red-300 px-4 py-2 text-sm font-semibold hover:bg-red-400/10 disabled:opacity-60"
                    >
                      <X size={15} /> Reject
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
